import React, { useState, useEffect } from "react";
import axios from "axios";

const UploadMedia = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null); // Success message
  const [uploadError, setUploadError] = useState(null); // Error message
  const [media, setMedia] = useState([]); // List of media items
  const [loadingMedia, setLoadingMedia] = useState(false); // Loading state for media list

  // Dynamically determine base URL
  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://joygardens-backend.onrender.com";

  useEffect(() => {
    fetchMedia();
  }, []);

  // Fetch media from the backend
  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/media`);
      setMedia(response.data.resources);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoadingMedia(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    setFile([...e.target.files]); // Allow multiple file selection
  };

  // Handle media upload
  const handleUpload = async () => {
    if (!file || file.length === 0) {
      alert("Please select at least one file to upload!");
      return;
    }

    const formData = new FormData();

    // Append each selected file to the FormData
    file.forEach((singleFile) => {
      formData.append("file", singleFile);
    });

    try {
      setUploading(true);
      setUploadSuccess(null);
      setUploadError(null);

      const response = await axios.post(`${BASE_URL}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadSuccess("Files uploaded successfully!");
      console.log("Upload Response:", response.data);

      // Refresh the media list
      fetchMedia();

      // Clear the file input
      setFile([]);
    } catch (error) {
      console.error("Error uploading media:", error);
      setUploadError("Failed to upload media. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle media deletion
  const handleDelete = async (publicId) => {
    try {
      // Extract the public_id without the folder name
      const fileName = publicId.includes("/")
        ? publicId.split("/").pop()
        : publicId;
  
      // Send DELETE request to the backend
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}/api/media/${fileName}`
      );
  
      if (response.status === 200) {
        setMedia((prevMedia) => prevMedia.filter((item) => item.public_id !== publicId));
      } else {
        throw new Error("Failed to delete media");
      }
    } catch (error) {
      console.error("Error deleting media:", error);
      alert("Failed to delete media. Please try again.");
    }
  };
  

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col items-center justify-start p-6 pt-20">
      {/* Upload Section */}
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md mb-10">
        <h2 className="text-2xl font-bold text-center mb-4">Upload Media</h2>

        {uploadSuccess && <p className="text-green-500 text-center mb-4">{uploadSuccess}</p>}
        {uploadError && <p className="text-red-500 text-center mb-4">{uploadError}</p>}

        <div className="mb-4">
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
          />
        </div>

        <button
          onClick={handleUpload}
          disabled={uploading}
          className={`w-full px-4 py-2 font-medium text-white rounded-lg shadow-md ${
            uploading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 transition"
          }`}
        >
          {uploading ? "Uploading..." : "Upload"}
        </button>
      </div>

      {/* Media Gallery */}
      <div className="container mx-auto">
        <h2 className="text-2xl font-bold text-center mb-6">Uploaded Media</h2>
        {loadingMedia ? (
          <p className="text-center text-gray-700">Loading media...</p>
        ) : media.length === 0 ? (
          <p className="text-center text-gray-700">No media available.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {media.map((item) => (
              <div
                key={item.asset_id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={item.secure_url}
                  alt={item.filename}
                  className="w-full h-48 object-cover cursor-pointer"
                  onDoubleClick={() => window.open(item.secure_url, "_blank")}
                />
                <div className="p-4 text-center">
                  <button
                    onClick={() => handleDelete(item.public_id)}
                    className="text-red-500 hover:underline text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadMedia;
